import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { doc, updateDoc } from 'firebase/firestore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import styles from 'AppRoutes/SubscriptionReview/styles';
import SubscriptionOrderSummary from 'components/SubscriptionOrderSummary';
import { analyticsService } from 'services/analytics/analyticsService';
import { auth, firestore } from 'services/firebase/client';
import {
  formatCurrencyBRL,
  parsePlanPrice,
  roundCurrency,
  sumCurrency,
} from 'utils/currency';
import {
  buildPlanKey,
  formatCep,
  loadSubscriptionShippingPreview,
} from 'utils/subscriptionShippingPreview';

const SubscriptionAddress = () => {
  const {
    selectedPlan,
    personalData,
    updatePersonalData,
    updateSubscription,
  } = useSubscription();
  const navigate = useNavigate();

  const [cep, setCep] = useState(personalData.cep || '');
  const [street, setStreet] = useState(personalData.address?.street || '');
  const [neighborhood, setNeighborhood] = useState(
    personalData.address?.neighborhood || personalData.neighborhood || '',
  );
  const [city, setCity] = useState(
    personalData.address?.city || personalData.city || '',
  );
  const [uf, setUf] = useState(personalData.address?.uf || personalData.uf || '');
  const [number, setNumber] = useState(
    personalData.number || personalData.address?.number || '',
  );
  const [complement, setComplement] = useState(
    personalData.complement || personalData.address?.complement || '',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const quantity = selectedPlan?.quantity || 1;
  const subtotal = useMemo(
    () => roundCurrency(parsePlanPrice(selectedPlan?.price) * quantity),
    [quantity, selectedPlan?.price],
  );

  const planKey = useMemo(() => buildPlanKey(selectedPlan), [selectedPlan]);
  const storedPreview = useMemo(
    () => (planKey ? loadSubscriptionShippingPreview(planKey) : null),
    [planKey],
  );
  const shippingValue = storedPreview?.quote.price ?? null;
  const total = sumCurrency(subtotal, shippingValue ?? 0);
  const packTitle = selectedPlan
    ? `${selectedPlan.label} - ${selectedPlan.type}`
    : '';

  useEffect(() => {
    analyticsService.trackPageView(
      '/subscription/address',
      'Endereço da Assinatura',
    );
    analyticsService.trackSubscriptionFlow(
      'address',
      packTitle || undefined,
      subtotal,
    );
  }, [packTitle, subtotal]);

  useEffect(() => {
    if (!selectedPlan) {
      navigate('/');
      return;
    }
    if (!auth.currentUser) {
      navigate('/subscription/account', { replace: true });
    }
  }, [navigate, selectedPlan]);

  const clearResolvedAddress = useCallback(() => {
    setStreet('');
    setNeighborhood('');
    setCity('');
    setUf('');
  }, []);

  const handleFetchCep = useCallback(async () => {
    const cepDigits = cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      setErrors(prev => ({
        ...prev,
        cep: 'Digite um CEP válido (8 dígitos).',
      }));
      clearResolvedAddress();
      return;
    }

    setCepLoading(true);
    setErrors(prev => ({ ...prev, cep: '' }));

    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${cepDigits}/json/`,
      );
      if (response.data.erro || !response.data.localidade) {
        setErrors(prev => ({ ...prev, cep: 'CEP não encontrado.' }));
        clearResolvedAddress();
        return;
      }

      setCep(formatCep(cepDigits));
      setStreet(response.data.logradouro || '');
      setNeighborhood(response.data.bairro || '');
      setCity(response.data.localidade || '');
      setUf(response.data.uf || '');
    } catch {
      setErrors(prev => ({
        ...prev,
        cep: 'Erro ao buscar o CEP. Tente novamente.',
      }));
      clearResolvedAddress();
    } finally {
      setCepLoading(false);
    }
  }, [cep, clearResolvedAddress]);

  const validate = useCallback(() => {
    const next: Record<string, string> = {};
    const cepDigits = cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      next.cep = 'Digite um CEP válido (8 dígitos).';
    } else if (!city || !uf) {
      next.cep = 'Busque um CEP válido antes de continuar.';
    }
    if (!number.trim()) {
      next.number = 'Informe o número';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [cep, city, number, uf]);

  const handleAdvance = useCallback(async () => {
    if (!selectedPlan) {
      return;
    }
    setFormError('');
    if (!validate()) {
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      navigate('/subscription/account', { replace: true });
      return;
    }

    const cepDigits = cep.replace(/\D/g, '');
    const addressPayload = {
      cep: formatCep(cepDigits),
      number: number.trim(),
      complement: complement.trim(),
      city,
      uf,
      neighborhood,
      address: {
        street,
        neighborhood,
        city,
        zip: cepDigits,
        number: number.trim(),
        complement: complement.trim(),
        reference: '',
        recipient: personalData.fullName || user.displayName || '',
        id: '',
        uf,
      },
    };

    setSubmitting(true);
    try {
      await updateDoc(doc(firestore, 'subscription_users', user.uid), {
        ...addressPayload,
        funnelStatus: 'address',
        updatedAt: new Date().toISOString(),
      });

      updatePersonalData(addressPayload);
      updateSubscription({
        ...selectedPlan,
        quantity,
        subtotal,
        total: subtotal,
      });

      analyticsService.trackCta(
        'subscription_address_submit',
        'Salvar endereço e continuar',
        { subscription_plan: packTitle || null },
      );
      analyticsService.trackSubscriptionFlow(
        'address_saved',
        packTitle,
        subtotal,
      );

      navigate('/shipping');
    } catch (error) {
      console.error('Erro ao salvar endereço:', error);
      setFormError('Não foi possível salvar o endereço. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }, [
    cep,
    city,
    complement,
    navigate,
    neighborhood,
    number,
    packTitle,
    personalData.fullName,
    quantity,
    selectedPlan,
    street,
    subtotal,
    uf,
    updatePersonalData,
    updateSubscription,
    validate,
  ]);

  if (!selectedPlan) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Grid container spacing={4} sx={styles.gridContainer}>
        <Grid item xs={12} sm={6}>
          <Card sx={styles.card}>
            <CardContent>
              <Typography variant="h5" sx={{ ...styles.title, mb: 2 }}>
                Endereço de entrega
              </Typography>

              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <InputMask
                    mask="99999-999"
                    value={cep}
                    onChange={e => {
                      setCep(e.target.value);
                      clearResolvedAddress();
                      setErrors(prev => ({ ...prev, cep: '' }));
                    }}
                    onBlur={() => {
                      void handleFetchCep();
                    }}
                    maskChar={null}
                  >
                    {(
                      inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                    ): React.ReactElement => {
                      const {
                        size: _size,
                        color: _color,
                        ...rest
                      } = inputProps;
                      return (
                        <TextField
                          {...rest}
                          fullWidth
                          label="CEP"
                          error={!!errors.cep}
                          helperText={errors.cep}
                          InputProps={{
                            endAdornment: cepLoading ? (
                              <CircularProgress size={20} />
                            ) : undefined,
                          }}
                        />
                      );
                    }}
                  </InputMask>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ height: '56px' }}
                    onClick={() => {
                      void handleFetchCep();
                    }}
                    disabled={cepLoading}
                  >
                    Buscar
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Logradouro"
                    value={street}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    value={neighborhood}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="UF"
                    value={uf}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    value={city}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={number}
                    onChange={e => setNumber(e.target.value)}
                    error={!!errors.number}
                    helperText={errors.number}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Complemento"
                    value={complement}
                    onChange={e => setComplement(e.target.value)}
                    error={!!errors.complement}
                    helperText={errors.complement || 'Apto, bloco, casa, etc.'}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={styles.title}>
              {packTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subtotal R$ {formatCurrencyBRL(subtotal)}
            </Typography>
          </Box>
          <SubscriptionOrderSummary
            subtotal={subtotal}
            shippingValue={shippingValue}
            shippingPreview={storedPreview?.quote ?? null}
            total={total}
            onAdvance={() => {
              void handleAdvance();
            }}
            advanceDisabled={submitting}
            advanceLoading={submitting}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate('/subscription')}
        >
          Voltar
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionAddress;
